import React, { useState } from 'react';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DialogContent from '@mui/material/DialogContent';
import { NavLink } from 'react-router-dom';
import Tableau from '../../../Tableau/Tableau';
import { displayAlert } from '../../../../slices/feedbackSlice';
import Search from '../../../Search/Search';
import AddCustomerModal from '../../Customers/AddCustomerModal/AddCustomerModal';
import { createLending } from '../../../../slices/itemsSlice';

const useStyles = makeStyles()((theme) => ({

  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  wrapper: {
    position: 'relative',
  },
  buttonProgress: {
    color: theme.palette.secondary.main,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  sharingButton: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  copyButton: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

export default function LendBookModal({ display, handleClose, book }) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation(['myBooks', 'customers']);
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [addCustomerModalOpen, setCustomerModalOpen] = useState(false);

  const isSubscribed = useSelector((state) => state.organization.organization.is_subscribed);
  const customers = useSelector((state) => state.customers.customers)
    .map((customer) => ({ ...customer }));

  const handleAddCustomerModalClose = () => {
    setCustomerModalOpen(false);
  };

  const handleAddCustomerModalOpen = () => {
    setCustomerModalOpen(true);
  };

  const handleLend = () => {
    const lendingData = {
      customer: selectedCustomer.id,
      book: book.id,
    };
    dispatch(createLending(lendingData))
      .unwrap()
      .then(
        () => {
          dispatch(displayAlert(t('lend_modal.success'), 'success'));
          handleClose();
        },
      )
      // ⛔ PAS de message générique par-dessus celui de l'API. Le thunk a déjà
      // posé `manageError(error)`, et `displayAlert` n'a qu'un seul emplacement :
      // le dernier écrit gagne. C'est l'écran que ferme le cloisonnement des prêts : y
      // prêter l'ouvrage ou le membre d'une autre bibliothèque rend un 400
      // que seul le message de l'API explique.
      .catch(() => {});
  };

  const handleSelectCustomer = (rows) => {
    const row = rows[0];
    setSelectedCustomer(row);
  };

  const filterCustomers = (query) => {
    const filtered = customers.filter((customer) => (
      ((customer.first_name || '').toLowerCase().includes(query.toLowerCase())
      || (customer.last_name || '').toLowerCase().includes(query.toLowerCase())
      || (customer.email || '').toString().includes(query.toLowerCase())
      || (customer.phone || '').toLowerCase().includes(query.toLowerCase()))));
    setFilteredCustomers(filtered.slice(0, 5));
  };

  const modalContent = (
    isSubscribed
      ? (
        // Subscribed
        <>
          <AddCustomerModal
            display={addCustomerModalOpen}
            handleClose={handleAddCustomerModalClose}
          />
          <DialogTitle className={classes.root}>
            <Typography type="h3" variant="h3">
              {t('lend_modal.title')}
            </Typography>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={handleClose}
              size="large"
            >
              <CloseIcon />
            </IconButton>

          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography type="p" variant="body1">
                  {t('lend_modal.find_customer')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Search onSearch={filterCustomers} />
              </Grid>
              <Grid item xs={12}>
                {!!filteredCustomers.length && (
                <Tableau
                  colonnes={[
                    { title: t('customers:firstName'), field: 'first_name' },
                    { title: t('customers:lastName'), field: 'last_name' },
                    { title: t('customers:email'), field: 'email' },
                    { title: t('customers:phoneNumber'), field: 'phone' },
                  ]}
                  donnees={filteredCustomers}
                  // Ni titre, ni recherche, ni pagination : la fenêtre a déjà
                  // son propre champ de recherche juste au-dessus, et la liste
                  // y est courte par construction.
                  titre={null}
                  recherche={false}
                  pagination={false}
                  selection
                  onSelectionChange={(rows) => handleSelectCustomer(rows)}
                />
                )}
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions>
            <Grid container alignItems="flex-end" justifyContent="flex-end" direction="row">
              <Grid item xs={6} className={classes.sharingButton}>
                <Button
                  variant="contained"
                  component="label"
                  color="primary"
                  onClick={handleAddCustomerModalOpen}
                >
                  {t('lend_modal.add_customer_button')}
                </Button>
              </Grid>
              <Grid item xs={6} className={classes.copyButton}>
                <Button
                  variant="contained"
                  component="label"
                  color="primary"
                  onClick={handleLend}
                >
                  {t('lend_modal.lend_button')}
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </>
      )
      : (

        // Not subscribed
        <>
          <DialogTitle className={classes.root}>
            {/* ⚠️ « lend_modal », pas « share_modal ». Ce bloc a été copié
                depuis la modale de partage et en avait gardé le titre : le
                bouton « Prêter » ouvrait une fenêtre intitulée « Partager ma
                collection », qui parle d'autre chose que ce qu'on a demandé. */}
            <Typography type="h3" variant="h3">
              {t('lend_modal.title')}
            </Typography>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={handleClose}
              size="large"
            >
              <CloseIcon />
            </IconButton>

          </DialogTitle>
          <DialogContent dividers>
            <Typography type="p" variant="body1">
              {t('lend_modal.no_subscription')}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Grid container alignItems="flex-end" justifyContent="flex-end" direction="row">
              <Grid item xs={6} className={classes.copyButton}>
                <Button
                  variant="contained"
                  color="primary"
                  component={NavLink}
                  to="myaccount"
                >
                  {t('lend_modal.subscribe')}
                </Button>
              </Grid>
            </Grid>

          </DialogActions>
        </>
      )
  );

  return (
    <Dialog
      open={display}
      onClose={handleClose}
      fullWidth
    >
      {modalContent}
    </Dialog>
  );
}
